import type { ResultRow } from '@commissionsight/sdk';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../api.js';
import { Button, Card, EmptyState, FlagChips, ICONS, Loading, StatPill } from '../components/ui.js';
import { downloadCsv, toCsv } from '../lib/csv.js';
import { explainRow, formatCurrency } from '../lib/status.js';

const PAGE = 100;

export function JobResults() {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const { client } = useAuth();
  const [rows, setRows] = useState<ResultRow[]>([]);
  // Honor a `?status=` deep link (e.g. the dashboard "Review" buttons land here
  // pre-filtered to red); the dropdown can still change it afterward.
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ?? '');
  // `?owed=1` deep link → show only members with a commission shortfall.
  const [owedOnly, setOwedOnly] = useState(searchParams.get('owed') === '1');
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [packetBusy, setPacketBusy] = useState(false);
  const parentRef = useRef<HTMLDivElement>(null);

  const loadPage = useCallback(
    async (offset: number, replace: boolean) => {
      if (!jobId) return;
      setLoading(true);
      try {
        const res = await client.getJobResults(jobId, {
          status: statusFilter || undefined,
          owedOnly: owedOnly || undefined,
          limit: PAGE,
          offset,
        });
        setRows((prev) => (replace ? res.data : [...prev, ...res.data]));
        setHasMore(res.pagination?.hasMore ?? false);
      } finally {
        setLoading(false);
      }
    },
    [client, jobId, statusFilter, owedOnly],
  );

  useEffect(() => {
    setRows([]);
    setHasMore(true);
    loadPage(0, true);
  }, [loadPage]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60,
    overscan: 12,
  });

  // Infinite scroll: load the next page near the bottom.
  const virtualItems = virtualizer.getVirtualItems();
  useEffect(() => {
    const last = virtualItems[virtualItems.length - 1];
    if (last && last.index >= rows.length - 10 && hasMore && !loading) {
      loadPage(rows.length, false);
    }
  }, [virtualItems, rows.length, hasMore, loading, loadPage]);

  const exportCsv = () => {
    const csv = toCsv(rows, [
      { key: 'memberRefId', header: 'Member Ref ID' },
      { key: 'policyRefId', header: 'Policy Ref ID' },
      { key: 'memberExternalId', header: 'Member ID' },
      { key: 'memberName', header: 'Name' },
      { key: 'status', header: 'Status' },
      { key: 'flags', header: 'Flags' },
      { key: 'commissionAmount', header: 'Commission' },
      { key: 'prevCommissionAmount', header: 'Prev Commission' },
      { key: 'commissionOwed', header: 'Owed' },
      { key: 'policyNumber', header: 'Policy No' },
      { key: 'planName', header: 'Plan' },
    ]);
    downloadCsv(`job-${jobId}-results.csv`, csv);
  };

  // Carrier dispute packet: EVERY underpaid member (paginated in full, not just
  // what's loaded on screen) with the defensible expected-vs-paid breakdown — the
  // artifact an agency attaches to a carrier dispute.
  const exportDisputePacket = async () => {
    if (!jobId || packetBusy) return;
    setPacketBusy(true);
    try {
      const all: ResultRow[] = [];
      let offset = 0;
      for (let guard = 0; guard < 1000; guard++) {
        const res = await client.getJobResults(jobId, { owedOnly: true, limit: 500, offset });
        all.push(...res.data);
        offset += res.data.length;
        if (!res.pagination?.hasMore || res.data.length === 0) break;
      }
      const out = all.map((r) => {
        const paid = r.commissionAmount ?? 0;
        const owed = r.commissionOwed ?? 0;
        return {
          memberExternalId: r.memberExternalId ?? r.memberRefId,
          memberName: r.memberName ?? '',
          planName: r.planName ?? '',
          policyNumber: r.policyNumber ?? '',
          commissionPaid: paid,
          expected: Math.round((paid + owed) * 100) / 100,
          owed,
        };
      });
      const csv = toCsv(out, [
        { key: 'memberExternalId', header: 'Member ID' },
        { key: 'memberName', header: 'Name' },
        { key: 'planName', header: 'Plan' },
        { key: 'policyNumber', header: 'Policy No' },
        { key: 'commissionPaid', header: 'Commission paid' },
        { key: 'expected', header: 'Expected (contracted)' },
        { key: 'owed', header: 'Shortfall owed' },
      ]);
      downloadCsv(`dispute-packet-${jobId}.csv`, csv);
    } finally {
      setPacketBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Results</h1>
        <div className="flex items-center gap-2">
          <select
            className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="red,yellow">At risk (red + yellow)</option>
            <option value="green">Green</option>
            <option value="yellow">Yellow</option>
            <option value="red">Red</option>
          </select>
          <label className="flex items-center gap-1.5 text-sm text-neutral-600 dark:text-neutral-300">
            <input
              type="checkbox"
              checked={owedOnly}
              onChange={(e) => setOwedOnly(e.target.checked)}
              className="h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500"
            />
            Underpaid only
          </label>
          <Button variant="ghost" onClick={exportCsv}>
            Export CSV
          </Button>
          <Button onClick={exportDisputePacket} loading={packetBusy}>
            Dispute packet
          </Button>
        </div>
      </div>

      <Card className="p-0">
        <div className="grid grid-cols-[1.3fr_0.7fr_2.4fr_1.1fr] gap-4 border-b border-neutral-200 px-5 py-2 text-xs font-medium text-neutral-500 dark:border-neutral-800">
          <span>Member</span>
          <span>Status</span>
          <span>Flags</span>
          <span className="text-right">Commission</span>
        </div>
        <div ref={parentRef} className="h-[60vh] overflow-auto">
          {rows.length === 0 && loading && <Loading label="Loading results…" />}
          {rows.length === 0 && !loading && (
            <EmptyState
              icon={ICONS.search}
              title="No results"
              hint="No members match this filter for the period."
            />
          )}
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
            {virtualItems.map((vi) => {
              const row = rows[vi.index];
              if (!row) return null;
              return (
                <div
                  key={row.memberRefId}
                  className="absolute left-0 grid w-full grid-cols-[1.3fr_0.7fr_2.4fr_1.1fr] items-center gap-4 border-b border-neutral-50 px-5 dark:border-neutral-800/50"
                  style={{ height: vi.size, transform: `translateY(${vi.start}px)` }}
                >
                  <div className="min-w-0">
                    <Link
                      to={`/members/${row.memberRefId}`}
                      className="truncate text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                      title="View this member's full history"
                    >
                      {row.memberName ?? row.memberExternalId ?? row.memberRefId.slice(0, 10)}
                    </Link>
                    <div className="truncate text-xs text-neutral-400">{row.memberExternalId}</div>
                  </div>
                  <StatPill status={row.status} />
                  <div className="min-w-0">
                    <FlagChips flags={row.flags} />
                    <div className="truncate text-xs text-neutral-400" title={explainRow(row)}>
                      {explainRow(row)}
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    {formatCurrency(row.commissionAmount)}
                    {row.prevCommissionAmount !== null &&
                      row.prevCommissionAmount !== row.commissionAmount && (
                        <div className="text-xs text-neutral-400">
                          was {formatCurrency(row.prevCommissionAmount)}
                        </div>
                      )}
                    {row.commissionOwed > 0 && (
                      <div
                        className="text-xs font-medium text-emerald-600 dark:text-emerald-400"
                        title="Expected (your contracted rate) minus what the carrier paid."
                      >
                        owed {formatCurrency(row.commissionOwed)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Card>
      <p className="text-xs text-neutral-400">
        {rows.length} loaded{hasMore ? ' · scroll for more' : ''}
      </p>
    </div>
  );
}
